import React, { useEffect, useRef, useState } from 'react'
import LPUIUXHeader from './LPHeader/UIUX/LPHeader'
import LPFrontendHeader from './LPHeader/Frontend/LPHeader'
import LPDesignSystemHeader from './LPHeader/DesignSystem/LPHeader'
import LPFlamingoHeader from './LPHeader/Flamingo/LPHeader'
import { useRouter } from 'next/router'
import { Icon, SvgSprite } from 'components/commons/HeaderFooter/Icon'
import BaseButton from 'components/commons/HeaderFooter/BaseButton'
import SiteMenu, { blankAttrs, delayStyle } from 'components/commons/HeaderFooter/SiteMenu'
import { hoverAttrs, useHoverEffect } from 'components/commons/HeaderFooter/useHoverEffect'
import { ButtonItem, NavItem } from 'components/commons/HeaderFooter/types'

const logo = {
  href: '/',
  src: '/img/common/logo_aitria.svg',
  alt: 'Aitria',
}

const navItems: NavItem[] = [
  {
    title: 'Services',
    children: [
      {
        title: 'AIDD',
        links: [
          { href: '/services/aidd/', title: 'AI駆動開発TOP', isBlank: true },
          { href: '/services/aidd/internalization/', title: 'AI駆動開発内製化サービス', isBlank: true },
          { href: '/services/aidd/ai-agent-infrastructure/', title: 'AIエージェント基盤構築サービス', isBlank: true },
          { href: '/services/aidd/training/', title: 'AI駆動開発研修', isBlank: true },
        ],
      },
      {
        title: 'STUDIO',
        links: [
          { href: '/services/studio/ux-sprint/', title: 'AI-UXスプリント', isBlank: true },
          { href: '/services/studio/ux-workshop/', title: 'UX/UIワークショップ', isBlank: true },
          { href: '/services/studio/application-development/', title: 'アプリケーション開発' },
          { href: '/services/studio/modernization/', title: 'モダナイゼーション' },
          { href: '/services/studio/uxui-improvement/', title: 'UX/UI改善' },
          { href: '/services/studio/design-system/', title: 'デザインシステム構築支援' },
        ],
      },
      {
        title: 'FORGE',
        links: [{ href: '/services/forge/cloudnative/', title: 'クラウドネイティブ' }],
      },
      {
        title: 'Products',
        links: [
          { href: 'https://phennec.ai/', title: 'Phennec', isBlank: true },
          { href: '/products/flamingo/', title: 'flamingo', isBlank: true },
          { href: '/products/zebra/', title: 'ZEBRA' },
          { href: 'https://eland.jp/', title: 'ELAND', isBlank: true },
        ],
      },
    ],
  },
  {
    href: '/cases/',
    title: 'Case study',
  },
  {
    href: '/about/',
    title: 'About',
    children: [
      {
        links: [{ href: '/mvv/', title: '当社が目指しているもの' }],
      },
    ],
  },
  {
    href: '/pressrelease/page/1/',
    title: 'Topics',
    children: [
      {
        links: [
          { href: '/pressrelease/page/1/', title: 'Press release' },
          { href: '/news/page/1/', title: 'News' },
          { href: '/events/page/1/', title: 'Event/Seminer' },
        ],
      },
    ],
  },
  {
    href: '/in-pocket/',
    title: 'Blog',
  },
  {
    href: 'https://aitria.careers/',
    title: 'Recruit',
    isBlank: true,
  },
]

// PC ヘッダー右側のボタン
const navCtaButtons: ButtonItem[] = [
  { href: '/download/', label: '資料ダウンロード', color: 'download' },
  { href: '/contact/', label: 'お問い合わせ', color: 'contact' },
]

// SP ドロワー内のボタン
const drawerCtaButtons: ButtonItem[] = [
  { href: '/contact/', label: 'お問い合わせ', color: 'contact' },
  { href: '/download/', label: '資料ダウンロード', color: 'download' },
  { href: '/mailmagazine/', label: 'メールマガジン', color: 'download' },
]

// SP ドロワー最下部のリンク（別タブ）
const drawerOtherLink = {
  href: 'https://tech.aitria.co.jp/',
  title: 'Tech Blog',
}

const HeaderLogo: React.FC = () => (
  <div className="prj-header__logo">
    <a href={logo.href} className="prj-header__logo--link no-icon">
      <img src={logo.src} alt={logo.alt} className="c-image__normal" />
    </a>
  </div>
)

const NavModal: React.FC<{ item: NavItem; isShow: boolean; onLeave: (e: React.MouseEvent) => void }> = ({
  item,
  isShow,
  onLeave,
}) => {
  const children = item.children || []
  const isServices = item.title === 'Services'
  const className = [
    'prj-header__modal c-effect__modal',
    children.length === 1 ? '--single' : '',
    isServices ? '--services' : '',
    isShow ? 'is-show' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} onMouseLeave={onLeave}>
      <div className="--content prj-header__modal--content">
        <div className="prj-header__modal--inner">
          <div className="prj-header__modal--heading">
            {isServices ? (
              <p className="--text prj-header__modal--title font-upgrade">{item.title}</p>
            ) : (
              <a {...hoverAttrs(400, 600)} href={item.href} className="prj-header__modal--link c-link__inline">
                <p className="--text prj-header__modal--title font-upgrade">{item.title}</p>
                <div className="prj-header__modal--icon">
                  <Icon name="arrow" viewBox="0 0 24 24" className="--icon c-icon__full" />
                </div>
              </a>
            )}
          </div>

          <div className="prj-header__modal--nav">
            {children.map((child, j) => (
              <div className="prj-header__modal--item" key={child.title || j}>
                {child.title && <p className="--text prj-header__modal--nav--title">{child.title}</p>}
                <ul className="prj-header__modal--nav--list">
                  {child.links.map((link, i) => (
                    <li
                      key={link.href}
                      className={`prj-header__modal--nav--item ${
                        i === 0 ? '--first' : i === child.links.length - 1 ? '--last' : ''
                      }`}
                    >
                      <a
                        {...hoverAttrs(400, 600)}
                        href={link.href}
                        className="prj-header__modal--nav--link c-link__inline"
                        {...blankAttrs(link.isBlank, link.href)}
                      >
                        <div className="prj-header__modal--nav--inner">
                          <p className="--text prj-header__modal--nav--text">{link.title}</p>
                          <div className="prj-header__modal--icon --sm">
                            {link.isBlank ? (
                              <Icon name="external" viewBox="0 0 20 20" className="--icon c-icon__full" />
                            ) : (
                              <Icon name="arrow" viewBox="0 0 24 24" className="--icon c-icon__full" />
                            )}
                          </div>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const NavLinkContent: React.FC<{ item: NavItem }> = ({ item }) =>
  item.isBlank ? (
    <span className="prj-header__nav--link-inner">
      <p className="--text prj-header__nav--text font-upgrade">{item.title}</p>
      <div className="prj-header__modal--icon --sm">
        <Icon name="external" viewBox="0 0 20 20" className="--icon c-icon__full" />
      </div>
    </span>
  ) : (
    <p className="--text prj-header__nav--text font-upgrade">{item.title}</p>
  )

const MainHeader: React.FC = () => {
  const router = useRouter()
  const headerRef = useRef<HTMLElement>(null)
  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  useHoverEffect(headerRef)

  useEffect(() => {
    const closeModal = () => setOpenModalIndex(null)
    document.body.addEventListener('mouseleave', closeModal)
    return () => document.body.removeEventListener('mouseleave', closeModal)
  }, [])

  useEffect(() => {
    setIsDrawerOpen(false)
    setOpenModalIndex(null)
  }, [router.asPath])

  const handleModalLeave = (e: React.MouseEvent) => {
    const navItem = (e.currentTarget as HTMLElement).parentElement
    if (navItem && navItem.matches(':hover')) return
    setOpenModalIndex(null)
  }

  return (
    <header ref={headerRef} className={`prj-header${isDrawerOpen ? ' is-open' : ''}`}>
      <div className="prj-header__container">
        <div className="prj-header__flex">
          <HeaderLogo />

          <div className="c-display__block--xl prj-header__nav">
            <nav className="prj-header__nav--inner">
              <ul className="prj-header__nav--list">
                {navItems.map((item, i) => {
                  const hasModal = !!item.children && item.children.length > 0
                  const linkClassName = `prj-header__nav--link c-link__nav${item.isBlank ? ' --has-external' : ''}`
                  return (
                    <li
                      key={item.title}
                      className="prj-header__nav--item"
                      onMouseEnter={() => setOpenModalIndex(hasModal ? i : null)}
                    >
                      {item.href ? (
                        <a
                          {...hoverAttrs(500, 500)}
                          href={item.href}
                          className={linkClassName}
                          {...blankAttrs(item.isBlank, item.href)}
                        >
                          <NavLinkContent item={item} />
                        </a>
                      ) : (
                        <button type="button" {...hoverAttrs(500, 500)} className={linkClassName}>
                          <NavLinkContent item={item} />
                        </button>
                      )}
                      {hasModal && <NavModal item={item} isShow={openModalIndex === i} onLeave={handleModalLeave} />}
                    </li>
                  )
                })}
              </ul>
              <div className="prj-header__nav--cta">
                {navCtaButtons.map((button) => (
                  <BaseButton key={button.href} {...button} />
                ))}
              </div>
            </nav>
          </div>

          <div className="c-display__hidden--xl prj-header__drawer">
            <button
              type="button"
              className="c-button__drawer c-effect__drawer"
              aria-label="メニュー"
              aria-expanded={isDrawerOpen}
              onClick={() => setIsDrawerOpen((prev) => !prev)}
            >
              <span className="c-button__drawer--inner">
                <span className="c-button__drawer--line --before" />
                <span className="c-button__drawer--line --center" />
                <span className="c-button__drawer--line --after" />
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="c-effect__menu c-display__hidden--xl prj-header__menu">
        <span className="prj-header__menu--mask" />
        <div className="prj-header__menu--inner">
          <span className="prj-header__menu--bg" />

          <SiteMenu items={navItems} isTop />

          <div className="--cta prj-header__menu--cta" style={delayStyle((navItems.length + 1) * 50)}>
            {drawerCtaButtons.map((button) => (
              <BaseButton key={button.href} {...button} hasIcon />
            ))}
          </div>

          <div className="--other prj-header__menu--other" style={delayStyle((navItems.length + 2) * 50)}>
            <a
              href={drawerOtherLink.href}
              className="prj-header__menu--other--link c-link"
              {...blankAttrs(true, drawerOtherLink.href)}
            >
              <p className="prj-header__menu--other--text">{drawerOtherLink.title}</p>
              <div className="prj-header__menu--other--icon">
                <Icon name="external" viewBox="0 0 20 20" className="c-icon__normal" />
              </div>
            </a>
          </div>
        </div>
      </div>

      <span
        className={`c-display__block--xl prj-header__mask c-effect__mask${openModalIndex !== null ? ' is-show' : ''}`}
      />
    </header>
  )
}

const Header: React.FC = () => {
  const router = useRouter()

  const isUIUXLpPage: boolean = new RegExp(/\/uiuxdesign-lp2|\/lp-ui-contact-completed/).test(router.pathname)
  const isDesignSystemLpPage: boolean = new RegExp(/\/designsystem|\/lp-designsystem-contact-completed/).test(
    router.pathname,
  )
  const isFrontendBackendLpPage: boolean = new RegExp(
    /\/services\/frontend-dev|\/lp-frontend-contact-completed|\/backend-dev|\/lp-backend-contact-completed/,
  ).test(router.pathname)
  const isAgreementPage: boolean = new RegExp(/\/agreement/).test(router.pathname)
  const isFlamingoPage: boolean = router.pathname.includes('/products/flamingo')
  const isContactPage: boolean = new RegExp(/\/contact\/?$/).test(router.pathname)
  const isLogoOnlyHeaderPage: boolean = isContactPage || isAgreementPage

  if (isUIUXLpPage) {
    return <LPUIUXHeader />
  }
  if (isFrontendBackendLpPage) {
    return <LPFrontendHeader />
  }
  if (isDesignSystemLpPage) {
    return <LPDesignSystemHeader />
  }
  if (isFlamingoPage) {
    return (
      <>
        <SvgSprite />
        <LPFlamingoHeader />
      </>
    )
  }
  if (isLogoOnlyHeaderPage) {
    return (
      <header className="prj-header">
        <div className="prj-header__container">
          <div className="prj-header__flex">
            <HeaderLogo />
          </div>
        </div>
      </header>
    )
  }
  return (
    <>
      <SvgSprite />
      <MainHeader />
    </>
  )
}

export default Header
